'use client';

import { logger } from '@adornis/base/logging.js';
import {
  ArrowLeft,
  Bell,
  Check,
  ChevronDown,
  Download,
  ExternalLink,
  FileText,
  Globe,
  LifeBuoy,
  Monitor,
  Moon,
  Scale,
  ShieldAlert,
  Sliders,
  Trash2,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setTeacherGradingKindnessMutation } from '../../../db/api/set-teacher-grading-kindness.js';
import { setTeacherLanguageMutation } from '../../../db/api/set-teacher-language.js';
import { SUPPORTED_LANGUAGES } from '../../../i18n.js';
import { useCurrentUserProfile } from '../../lib/useCurrentUserProfile.js';
import { Button } from '../ui/button.js';

interface SettingsPageProps {
  onBack: () => void;
}

// --- Select Component ---
function CustomSelect({
  value,
  onChange,
  options,
  disabled = false,
}: {
  value: string;
  onChange: (val: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div className="relative min-w-[160px]" ref={dropdownRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-3 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary text-sm text-foreground transition-all shadow-sm ${
          disabled ? 'opacity-50 cursor-not-allowed bg-muted' : 'hover:bg-accent hover:text-accent-foreground'
        }`}
      >
        <span>{selectedOption?.label}</span>
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1.5 bg-popover border border-border rounded-lg shadow-md py-1 animate-in fade-in zoom-in-95 duration-100">
          {options.map(option => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center justify-between ${
                value === option.value
                  ? 'bg-brand-surface text-brand-text font-medium'
                  : 'text-popover-foreground hover:bg-accent hover:text-accent-foreground'
              }`}
            >
              {option.label}
              {value === option.value && <Check className="h-4 w-4 text-brand-text" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Helper for external Links  ---
function ExternalLinkItem({ icon: Icon, title, url }: { icon: any; title: string; url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center justify-between p-3 sm:p-4 border border-border rounded-lg hover:bg-accent hover:text-accent-foreground transition-all group"
    >
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 text-muted-foreground group-hover:text-brand-text transition-colors" />
        <span className="font-medium text-foreground">{title}</span>
      </div>
      <ExternalLink className="h-4 w-4 text-muted-foreground/50 group-hover:text-brand-text transition-colors" />
    </a>
  );
}

export function SettingsPage({ onBack }: SettingsPageProps) {
  const { t, i18n } = useTranslation();
  const { role } = useCurrentUserProfile();

  const [emailNotifications, setEmailNotifications] = useState(false);
  const [browserNotifications, setBrowserNotifications] = useState(false);
  const [aiStrictness, setAiStrictness] = useState('normal');
  const [gradingKindness, setGradingKindness] = useState('balanced');

  // --- GRADING KINDNESS ---
  useEffect(() => {
    const saved = localStorage.getItem('gradingKindness') || 'balanced';
    setGradingKindness(saved);
  }, []);

  const handleGradingKindnessChange = (val: string) => {
    setGradingKindness(val);
    localStorage.setItem('gradingKindness', val);
    void setTeacherGradingKindnessMutation(val)().catch(err => {
      logger.error(err, 'Failed to persist gradingKindness preference');
    });
  };

  // --- THEME LOGIK ---
  const [theme, setTheme] = useState('system');

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'system';
    setTheme(savedTheme);
  }, []);

  useEffect(() => {
    const root = window.document.documentElement;
    const applyTheme = (currentTheme: string) => {
      root.classList.remove('light', 'dark');
      if (currentTheme === 'system') {
        const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        root.classList.add(systemTheme);
      } else {
        root.classList.add(currentTheme);
      }
    };

    applyTheme(theme);
    localStorage.setItem('theme', theme);

    // Listener für System-Wechsel (wenn auf "System" gestellt)
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (theme === 'system') applyTheme('system');
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  const Toggle = ({
    checked,
    onChange,
    disabled = false,
  }: {
    checked: boolean;
    onChange: () => void;
    disabled?: boolean;
  }) => (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      className={`w-11 h-6 rounded-full transition-colors relative focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 focus:ring-offset-background ${
        checked ? 'bg-brand-strong' : 'bg-muted border border-border'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <span
        className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full transition-transform shadow-sm ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );

  const SoonBadge = () => (
    <span className="ml-2 text-[10px] font-semibold bg-muted text-muted-foreground px-2 py-0.5 rounded uppercase tracking-wide border border-border/50">
      {t('Settings.soon')}
    </span>
  );

  return (
    <div className="flex flex-col h-full w-full bg-background overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 border-b border-border bg-card px-4 sm:px-8 py-2 sm:py-4 shadow-sm">
        <div className="flex items-center justify-start gap-2 max-w-4xl mx-auto">
          <Button
            onClick={onBack}
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-foreground hover:bg-accent"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl sm:text-xl font-bold text-foreground tracking-tight">{t('Settings.title')}</h1>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-4 sm:px-8 py-8">
        <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in duration-500">
          {/* Languague & Theme */}
          <section className="bg-card rounded-xl p-6 sm:p-8 border border-border shadow-sm">
            <h2 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
              <Monitor className="h-5 w-5 text-brand-text" /> {t('Settings.DisplayAndLanguage.title')}
            </h2>
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <p className="font-medium text-foreground flex items-center gap-2">
                    <Globe className="h-4 w-4 text-muted-foreground" /> {t('Settings.DisplayAndLanguage.languageLabel')}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">{t('Settings.DisplayAndLanguage.languageDesc')}</p>
                </div>
                <CustomSelect
                  disabled={false}
                  value={i18n.language}
                  onChange={newLang => {
                    void setTeacherLanguageMutation(newLang)().catch(err => {
                      logger.error(err, 'Failed to persist language preference');
                    });
                    void i18n.changeLanguage(newLang);
                  }}
                  options={SUPPORTED_LANGUAGES}
                />
              </div>

              <div className="h-px bg-border/50" />

              {/* Theme Toggle (Jetzt aktiv!) */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <p className="font-medium text-foreground flex items-center gap-2">
                    <Moon className="h-4 w-4 text-muted-foreground" /> {t('Settings.DisplayAndLanguage.themeLabel')}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">{t('Settings.DisplayAndLanguage.themeDesc')}</p>
                </div>
                <CustomSelect
                  disabled={false}
                  value={theme}
                  onChange={setTheme}
                  options={[
                    { value: 'light', label: 'Light' },
                    { value: 'dark', label: 'Dark' },
                    { value: 'system', label: 'System' },
                  ]}
                />
              </div>
            </div>
          </section>

          {/* AI behaviour — grading-specific, teachers only */}
          {role !== 'student' && (
            <section className="bg-card rounded-xl p-6 sm:p-8 border border-border shadow-sm">
              <h2 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
                <Sliders className="h-5 w-5 text-brand-text" /> {t('Settings.AI.title')}
              </h2>
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="flex-1 pr-0 sm:pr-4">
                    <p className="font-medium text-foreground flex items-center">
                      {t('Settings.AI.strictnessLabel')} <SoonBadge />
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t('Settings.AI.strictnessDesc')}
                      <span className="block mt-1">{t('Settings.AI.strictnessWarning')}</span>
                    </p>
                  </div>
                  <div className="mt-2 sm:mt-0">
                    <CustomSelect
                      disabled={true}
                      value={aiStrictness}
                      onChange={setAiStrictness}
                      options={[
                        { value: 'strict', label: 'Viel Review (Sicher)' },
                        { value: 'normal', label: 'Ausgewogen (Standard)' },
                        { value: 'lenient', label: 'Hohe Autonomie (Risiko)' },
                      ]}
                    />
                  </div>
                </div>

                <div className="h-px bg-border/50" />

                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="flex-1 pr-0 sm:pr-4">
                    <p className="font-medium text-foreground">{t('Settings.AI.gradingKindnessLabel')}</p>
                    <p className="text-sm text-muted-foreground mt-1">{t('Settings.AI.gradingKindnessDesc')}</p>
                  </div>
                  <div className="mt-2 sm:mt-0">
                    <CustomSelect
                      disabled={false}
                      value={gradingKindness}
                      onChange={handleGradingKindnessChange}
                      options={[
                        { value: 'lenient', label: t('Settings.AI.gradingKindnessOptions.lenient') },
                        { value: 'balanced', label: t('Settings.AI.gradingKindnessOptions.balanced') },
                        { value: 'strict', label: t('Settings.AI.gradingKindnessOptions.strict') },
                      ]}
                    />
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Notifications */}
          <section className="bg-card rounded-xl p-6 sm:p-8 border border-border shadow-sm">
            <h2 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
              <Bell className="h-5 w-5 text-brand-text" /> {t('Settings.Notifications.title')}
            </h2>
            <div className="space-y-6">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-medium text-foreground flex items-center">
                    {t('Settings.Notifications.emailLabel')} <SoonBadge />
                  </p>
                  <p className="text-sm text-muted-foreground mt-1"> {t('Settings.Notifications.emailLabel')}</p>
                </div>
                <Toggle
                  disabled={true}
                  checked={emailNotifications}
                  onChange={() => setEmailNotifications(!emailNotifications)}
                />
              </div>

              <div className="h-px bg-border/50" />

              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-medium text-foreground flex items-center">
                    {t('Settings.Notifications.browserLabel')} <SoonBadge />
                  </p>
                  <p className="text-sm text-muted-foreground mt-1"> {t('Settings.Notifications.browserDesc')}</p>
                </div>
                <Toggle
                  disabled={true}
                  checked={browserNotifications}
                  onChange={() => setBrowserNotifications(!browserNotifications)}
                />
              </div>
            </div>
          </section>

          {/* About */}
          <section className="bg-card rounded-xl p-6 sm:p-8 border border-border shadow-sm">
            <h2 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
              <Globe className="h-5 w-5 text-brand-text" /> {t('Settings.About.title')}
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ExternalLinkItem icon={Globe} title={t('Settings.About.landingPage')} url="https://gradias.ai" />
              <ExternalLinkItem icon={LifeBuoy} title={t('Settings.About.support')} />
              <ExternalLinkItem
                icon={FileText}
                title={t('Settings.About.privacy')}
                url="https://gradias.ai/datenschutz"
              />
              <ExternalLinkItem icon={Scale} title={t('Settings.About.imprint')} url="https://gradias.ai/imprint" />
            </div>
          </section>

          {/* Account */}
          <section className="bg-card rounded-xl p-6 sm:p-8 border border-border shadow-sm overflow-hidden">
            <h2 className="text-lg font-semibold text-destructive mb-6 flex items-center gap-2">
              <ShieldAlert className="h-5 w-5" /> {t('Settings.Account.title')}
            </h2>

            <div className="space-y-3">
              <div className="p-4 border border-border bg-background rounded-lg flex items-center justify-between opacity-50 cursor-not-allowed">
                <div className="flex items-center gap-4">
                  <Download className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium text-foreground flex items-center">
                      {t('Settings.Account.exportLabel')} <SoonBadge />
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">{t('Settings.Account.exportDesc')}</p>
                  </div>
                </div>
              </div>

              <div className="p-4 border border-destructive/20 bg-destructive/5 rounded-lg flex items-center justify-between opacity-50 cursor-not-allowed">
                <div className="flex items-center gap-4">
                  <Trash2 className="h-5 w-5 text-destructive" />
                  <div>
                    <p className="font-medium text-destructive flex items-center">
                      {t('Settings.Account.deleteLabel')} <SoonBadge />
                    </p>
                    <p className="text-sm text-destructive/80 mt-1">{t('Settings.Account.deleteDesc')}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
